import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/Footer';
import Icon from '../../components/AppIcon';
import SkillCard from './components/SkillCard';
import SkillModal from './components/SkillModal';
import TechStackBuilder from './components/TechStackBuilder';
import SkillTimeline from './components/SkillTimeline';
import LearningProgress from './components/LearningProgress';
import OpenSourceContributions from './components/OpenSourceContributions';
import { skillsData, skillCategories } from '../../data/skills';

const TechnicalSkillsJourney = () => {
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [activeCategory, setActiveCategory] = useState('All');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const filteredSkills = activeCategory === 'All' 
    ? skillsData 
    : skillsData.filter(skill => skill.category === activeCategory);

  const handleSkillClick = (skill) => {
    setSelectedSkill(skill);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedSkill(null);
  };

  const skillStats = {
    totalSkills: skillsData.length,
    expertLevel: skillsData.filter(s => s.proficiency === 'Expert').length,
    currentlyLearning: skillsData.filter(s => s.isLearning).length,
    totalProjects: skillsData.reduce((sum, skill) => sum + skill.projects, 0)
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Technical Skills & Journey - Arvin Zojaji's Portfolio</title>
        <meta name="description" content="Explore my technical expertise, skill development journey, and continuous learning path in web development, cloud technologies, and software engineering." />
        <meta name="keywords" content="technical skills, web development, React, Node.js, AWS, DevOps, programming, software engineering" />
      </Helmet>
      <Header />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-16 px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="text-4xl lg:text-5xl font-bold text-text-primary mb-4">
                Technical Skills & Journey
              </h1>
              <p className="text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
                An interactive exploration of my technical expertise, continuous learning journey, 
                and the evolution of my development skills over time.
              </p>
            </div>

            {/* Skills Overview Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
              <div className="bg-white rounded-xl p-6 text-center shadow-soft border border-border">
                <div className="w-12 h-12 bg-accent rounded-lg flex items-center justify-center mx-auto mb-3">
                  <Icon name="Code2" size={24} color="white" />
                </div>
                <div className="text-2xl font-bold text-accent mb-1">{skillStats.totalSkills}</div>
                <div className="text-sm text-text-secondary">Total Skills</div>
              </div>
              <div className="bg-white rounded-xl p-6 text-center shadow-soft border border-border">
                <div className="w-12 h-12 bg-success rounded-lg flex items-center justify-center mx-auto mb-3">
                  <Icon name="Award" size={24} color="white" />
                </div>
                <div className="text-2xl font-bold text-success mb-1">{skillStats.expertLevel}</div>
                <div className="text-sm text-text-secondary">Expert Level</div>
              </div>
              <div className="bg-white rounded-xl p-6 text-center shadow-soft border border-border">
                <div className="w-12 h-12 bg-warning rounded-lg flex items-center justify-center mx-auto mb-3">
                  <Icon name="BookOpen" size={24} color="white" />
                </div>
                <div className="text-2xl font-bold text-warning mb-1">{skillStats.currentlyLearning}</div>
                <div className="text-sm text-text-secondary">Currently Learning</div>
              </div>
              <div className="bg-white rounded-xl p-6 text-center shadow-soft border border-border">
                <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center mx-auto mb-3">
                  <Icon name="FolderOpen" size={24} color="white" />
                </div>
                <div className="text-2xl font-bold text-primary mb-1">{skillStats.totalProjects}</div>
                <div className="text-sm text-text-secondary">Total Projects</div>
              </div>
            </div>
          </div>
        </section>

        {/* Skills Matrix */}
        <section className="py-16 px-6 lg:px-8 bg-muted">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-text-primary mb-4">Interactive Skills Matrix</h2>
              <p className="text-text-secondary mb-6">
                Click on any skill to explore detailed information, project examples, and learning resources.
              </p>

              {/* Category Filter */}
              <div className="flex flex-wrap gap-2">
                {skillCategories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setActiveCategory(category)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                      activeCategory === category
                        ? 'bg-accent text-white shadow-soft'
                        : 'bg-white text-text-secondary hover:text-text-primary hover:bg-white/80'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            {/* Skills Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredSkills.map((skill) => (
                <SkillCard
                  key={skill.id}
                  skill={skill}
                  onClick={handleSkillClick}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Tech Stack Builder */}
        <section className="py-16 px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <TechStackBuilder />
          </div>
        </section>

        {/* Skills Timeline */}
        <section className="py-16 px-6 lg:px-8 bg-muted">
          <div className="max-w-7xl mx-auto">
            <SkillTimeline />
          </div>
        </section>

        {/* Learning Progress */}
        <section className="py-16 px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <LearningProgress />
          </div>
        </section>

        {/* Open Source Contributions */}
        <section className="py-16 px-6 lg:px-8 bg-muted">
          <div className="max-w-7xl mx-auto">
            <OpenSourceContributions />
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-16 px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="bg-gradient-to-r from-accent to-primary rounded-2xl p-8 text-white">
              <h2 className="text-3xl font-bold mb-4">Let's Build Something Amazing Together</h2>
              <p className="text-xl mb-6 opacity-90">
                Ready to leverage these skills for your next project? Let's discuss how my expertise 
                can help bring your ideas to life.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/contact-opportunities"
                  className="inline-flex items-center justify-center space-x-2 bg-white text-accent px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-300"
                >
                  <Icon name="MessageCircle" size={20} />
                  <span>Start a Conversation</span>
                </a>
                <a
                  href="/project-showcase-technical-case-studies"
                  className="inline-flex items-center justify-center space-x-2 border-2 border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white hover:text-accent transition-colors duration-300"
                >
                  <Icon name="FolderOpen" size={20} />
                  <span>View My Projects</span>
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      {/* Skill Detail Modal */}
      <SkillModal
        skill={selectedSkill}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default TechnicalSkillsJourney;
